import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import getClient from "../../../helpers/get-client";
import ControlCluster from "../../common/ControlCluster";
import { LimiteParte, lazyConReintento } from "../../../helpers/lazy-con-reintento";

// #1717 R4: el visor trae pdf.js (~0,5 MB) y se baja al abrir el primer PDF, no al arrancar la app.
// Si no baja (sin red), queda en el mismo error que ya mostraba este diálogo cuando PDF.js fallaba:
// la app sigue en pantalla (F5 del revisor v2). Un corte de red no recarga la app (F7).
const PdfCanvasViewer = lazyConReintento(() => import("./PdfCanvasViewer"));

/**
 * PdfPreviewDialog — vista previa full-height (Opción B) del PDF de un examen,
 * servido desde nuestro server (/api/medical_analysis/exams/pdf/) SIN login de
 * Proactive. Solo previsualiza: scroll + botón cerrar, sin descarga.
 *
 * El endpoint del PDF exige token por header, así que un <iframe src="/api/…">
 * daría 401. Por eso fetcheamos el PDF con axios (que sí inyecta el token) como
 * blob y lo embebemos con un object URL. Mismo patrón que PrescriptionModal.
 *
 * Montado a nivel App (fuera del viewer) vía [[PdfPreviewContext]] para quedar
 * POR ENCIMA del Dialog del visualizador — un Dialog anidado quedaba atrás en
 * el build de prod pese al z-index (mismo fix que el popup de ácido-base).
 */

// "2026-02-14 06:24" -> "14-02-2026 06:24"
function formatExamDate(raw) {
  if (!raw) return "";
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}))?/);
  if (!m) return String(raw);
  const [, y, mo, d, hm] = m;
  return `${d}-${mo}-${y}${hm ? ` ${hm}` : ""}`;
}

const PdfPreviewDialog = ({ open, pdf, onClose }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const blobUrlRef = useRef("");

  const revoke = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = "";
    }
  };

  useEffect(() => {
    // Fetch del PDF cada vez que se abre con una url nueva. Se limpia el blob
    // anterior para no acumular object URLs.
    if (!open || !pdf?.url) return undefined;
    let cancelled = false;
    revoke();
    setBlobUrl("");
    setError("");
    setLoading(true);
    getClient()
      .get(pdf.url, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setError("No se pudo cargar el PDF de este examen.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // pdf.url identifica de forma única el examen/fecha clickeado; reloadKey
    // fuerza un re-fetch del blob al tocar "Recargar".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pdf?.url, reloadKey]);

  // Al cerrar del todo, liberar el blob y volver al tamaño normal.
  useEffect(() => {
    if (!open) {
      revoke();
      setExpanded(false);
    }
    return () => revoke();
  }, [open]);

  const dateLabel = formatExamDate(pdf?.date);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile || expanded}
      maxWidth="md"
      fullWidth
      // Montado a nivel App (fuera del viewer). z-index EXTREMO con !important
      // para ganar sí o sí sobre el viewer (Dialog, z-index 1300) en el build de
      // prod, sin depender del orden de inserción de estilos ni del ModalManager
      // de MUI. Mismo criterio que AcidBaseAnalysisDialog.
      sx={{ zIndex: "2147483000 !important" }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(var(--shadow-rgb), 0.7)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: "var(--bg-elevated)",
          color: "var(--text-secondary)",
          backgroundImage: "none",
          border: "1px solid rgba(var(--fg-rgb), 0.08)",
          boxShadow: "0 24px 60px rgba(var(--shadow-rgb), 0.55)",
          height: isMobile || expanded ? "100dvh" : "90vh",
          maxHeight: isMobile || expanded ? "100dvh" : "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header contextual: ícono + nombre del examen + fecha, botón cerrar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          borderBottom: "1px solid rgba(var(--fg-rgb), 0.08)",
          flex: "none",
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(var(--color-primary-rgb), 0.14)",
            color: "var(--color-primary-soft)",
            flex: "none",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {pdf?.name || "Vista previa de examen"}
          </Typography>
          {(dateLabel || pdf?.value) && (
            <Typography
              sx={{
                fontSize: 11,
                color: "var(--text-faint)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {[dateLabel, pdf?.value].filter(Boolean).join(" · ")}
            </Typography>
          )}
        </Box>
        <ControlCluster
          actions={[
            {
              key: "reload",
              icon: <RefreshIcon />,
              label: "Recargar",
              onClick: () => setReloadKey((k) => k + 1),
              disabled: loading,
            },
            !isMobile && {
              key: "expand",
              icon: expanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />,
              label: expanded ? "Restaurar" : "Expandir",
              onClick: () => setExpanded((v) => !v),
            },
            {
              key: "close",
              icon: <CloseIcon />,
              label: "Cerrar",
              onClick: onClose,
              tone: "danger",
            },
          ]}
        />
      </Box>

      {/* Cuerpo: el PDF (scroll interno del visor) domina la pantalla */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          bgcolor: "rgba(var(--shadow-rgb), 0.35)",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              color: "var(--text-muted)",
            }}
          >
            <CircularProgress size={28} sx={{ color: "var(--color-primary-soft)" }} />
            <Typography sx={{ fontSize: 12.5 }}>Cargando PDF…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              textAlign: "center",
              color: "var(--text-muted)",
            }}
          >
            <Typography sx={{ fontSize: 13 }}>{error}</Typography>
          </Box>
        )}
        {!error && blobUrl && (
          // Renderizado con PDF.js (no iframe nativo): salta a la página del
          // examen y scrollea libre igual en desktop y mobile. key con expanded
          // fuerza re-fit del ancho al expandir/restaurar.
          <LimiteParte onFallo={() => setError("No se pudo mostrar el PDF de este examen.")}>
            <Suspense
              fallback={
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              }
            >
              <PdfCanvasViewer
                key={`${blobUrl}:${expanded}`}
                src={blobUrl}
                page={pdf?.page}
                onError={() => setError("No se pudo mostrar el PDF de este examen.")}
              />
            </Suspense>
          </LimiteParte>
        )}
      </Box>

      {/* Footer sutil: avisa que hay scroll, sin acciones de descarga */}
      <Box
        sx={{
          flex: "none",
          px: 2,
          py: 0.75,
          borderTop: "1px solid rgba(var(--fg-rgb), 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          fontSize: 11,
          color: "var(--text-faint)",
        }}
      >
        Vista previa · scroll para ver todo
      </Box>
    </Dialog>
  );
};

export default PdfPreviewDialog;
